import React, { Fragment, Suspense, useEffect, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import wayfinderIcon from "../../assets/icons/wayfinder-input.png";
import Layout from "../../components/Layout";
import searchIcon from "../../assets/icons/search.svg";
import xCircleIcon from "../../assets/icons/x-circle.svg";
import arrowDownIcon from "../../assets/icons/arrow-down.svg";
import arrowUpIcon from "../../assets/icons/arrow-up.png";
import repeat from "../../assets/icons/repeat.png";

import { useStores } from "../../use.store";
import CardPoi from "../../components/CardPoi";
import {
  AdsignMap,
  checkPathBetweenShape,
  getPathBetwenShapes,
  getPoiShapes,
  postGoToAnalytic,
  postSelectAnalytic,
} from "@adactiveasia/adsign-map";
import { observer } from "mobx-react-lite";
import NoResultFoundComponent from "../../components/NoResultFoundComponent";
import ModalDetailPoi from "../../components/ModalDetailPoi";
import ModalStartLocationFromQR from "../../components/ModalStartLocationFromQR";

const WayfinderPage = observer(() => {
  const interactiveRef = useRef(null);
  const cameraControlsRef = useRef(null);
  const sceneRef = useRef(null);
  const searchRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [isShowWayfinding, setIsShowWayfinding] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const [directionIndex, setDirectionIndex] = useState(0);
  const [searchValue, setSearchValue] = useState();
  const [isOpenWayfindingModal, setIsOpenWayfindingModal] = useState(false);
  const [openModalStartLoc, setOpenModalStartLoc] = useState(false);

  const [init, setInit] = useState(true);

  // use to handle screen state
  const [openModalPoi, setOpenModalPoi] = useState(false);

  const { mainStore, mapStore } = useStores();
  const filteredPois = mapStore.pois
    .filter((poi) => {
      return !poi?.name?.toLowerCase().includes("information");
    })
    .filter((poi) => {
      return !poi?.categoryId
        ?.toLowerCase()
        .includes("689c39aafc2a7f7a92ae0f9f");
    })
    .filter((poi) => {
      if (!searchValue) return true;

      const search = searchValue?.toLowerCase();
      const nameMatch = poi?.name?.toLowerCase().includes(search);

      return nameMatch;
    });

  const itemsPerPage = 6;
  const totalPages = Math.ceil(filteredPois.length / itemsPerPage);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const currentItems =
    filteredPois?.slice(indexOfFirstItem, indexOfLastItem) || [];

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPrevious = () => goToPage(currentPage - 1);
  const goToNext = () => goToPage(currentPage + 1);

  const renderPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <div
          key={i}
          onClick={() => goToPage(i)}
          className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
            currentPage === i
              ? "bg-[#215732] text-white"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          {i}
        </div>,
      );
    }
    return pages;
  };

  const startWayfinding = async () => {
    mainStore.setShowNavigateLoading(true);
    const path = checkPathBetweenShape(
      mapStore,
      mapStore.startShape._id,
      mapStore.destinationShape._id,
      "standard",
    );
    if (path) {
      setIsShowWayfinding(true);
      setSearchValue(null);
      setIsOpenWayfindingModal(false);
      setTimeout(() => {
        postGoToAnalytic(mapStore);
      }, 1000);

      try {
        mapStore.setExpanded(true);

        await getPathBetwenShapes(
          mapStore,
          mapStore.startShape,
          mapStore.destinationShape,
          "standard",
        );
      } catch (error) {
        console.log(error);
      } finally {
        mainStore.setShowNavigateLoading(false);
      }
    } else {
      alert("There is no path");
    }
  };

  const resetWayfinding = () => {
    mainStore.setSelectedStartPoi(null);
    mainStore.setSelectedStartShape(null);
    mainStore.setSelectedDestinationPoi(null);
    mainStore.setSelectedDestinationShape(null);

    mapStore.setStartPOI(null);
    mapStore.setStartShape(null);

    mapStore.setDestinationPOI(null);
    mapStore.setDestinationShape(null);

    mapStore.setCurrentFreeShape([]);

    // interactiveRef?.current?.resetMap();
    // mapStore?.resetMap();

    setIsShowWayfinding(false);
    setDirectionIndex(0);

    // if (mainStore.selectedStartPoi) {
    //   mapStore.setStartPOI(mainStore.selectedStartPoi);
    // }
    // if (mainStore.selectedStartShape) {
    //   mapStore.setStartShape(mainStore.selectedStartShape);
    // }
  };

  const [isFocused, setIsFocused] = useState(false);
  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  useEffect(() => {
    if (mapStore.pois) {
      if (!init) return false;
      const queryParams = new URLSearchParams(location.search);
      // const poiIdParams = queryParams?.get("poiId");
      const poiDestinationParams = queryParams?.get("poiDestination");

      const poiDestination =
        mapStore.pois &&
        mapStore.pois.find(({ _id }) => _id === poiDestinationParams);

      if (poiDestination) {
        const freeShape = getPoiShapes(mapStore, poiDestination._id)[0];

        if (freeShape) {
          if (interactiveRef?.current) {
            interactiveRef?.current.resetMap();
          }
          mapStore.resetMap();

          mapStore.setDestinationPOI(poiDestination);
          mapStore.setDestinationShape(freeShape);
          mainStore.setSelectedDestinationPoi(poiDestination);
          mainStore.setSelectedDestinationShape(freeShape);
        }
      }

      if (mainStore.selectedStartPoi) {
        mapStore.setStartPOI(mainStore.selectedStartPoi);
      }
      if (mainStore.selectedStartShape) {
        mapStore.setStartShape(mainStore.selectedStartShape);
      }
      setInit(false);
    }
  }, [
    mapStore.pois,
    mapStore.isMapLoaded,
    interactiveRef,
    mapStore.freeShapes,
  ]);

  useEffect(() => {
    if (mapStore.startPOI && mapStore.destinationPOI) {
      setOpenModalPoi(true);
      setIsOpenWayfindingModal(true);
    }
  }, [mapStore.startPOI, mapStore.destinationPOI]);

  useEffect(() => {
    if (mainStore.selectedStartPoi) {
      setOpenModalStartLoc(true);
    }
  }, []);

  return (
    <Layout hideBgImg={isShowWayfinding}>
      {isShowWayfinding && mapStore ? (
        <div className={`relative flex-1 flex items-center justify-center `}>
          {mapStore?.directions && (
            <div
              className={`absolute z-[40] right-2 ${
                isMinimized ? "h-16" : "h-[260px]"
              }`}
              style={{ bottom: "10px" }}
            >
              <div
                className={`w-[220px] rounded-xl transition-all duration-300 ${
                  isMinimized ? "h-16" : "h-[260px]"
                }`}
              >
                <div className="rounded-2xl mb-2 bg-[#F1EEE7] border-[0.7] border-[#C4C4C4] overflow-y-scroll h-full">
                  <div className="text-white p-2 rounded-lg h-[220px] overflow-y-auto">
                    <div className="flex justify-around items-center rounded-full p-2 md:p-3 bg-[#215732]">
                      <div className="flex " />
                      <span className="text-white text-xs font-bold">
                        DIRECTORY
                      </span>
                      <button
                        className="text-white text-xl focus:outline-none"
                        onClick={() => setIsMinimized(!isMinimized)}
                      >
                        <img
                          src={isMinimized ? arrowUpIcon : arrowDownIcon}
                          className="w-4 h-2"
                        />
                      </button>
                    </div>

                    {/* Direction Steps (Hidden when Minimized) */}
                    {!isMinimized && (
                      <div className="max-w-[220px] px-1">
                        <div className="mt-4 space-y-2 text-xs md:text-sm">
                          {mapStore.directions.map((direction, index) => (
                            <Fragment key={index}>
                              <div
                                onClick={() => {
                                  try {
                                    setDirectionIndex(index);
                                    interactiveRef.current.cameraFocusOnDirection(
                                      mapStore,
                                      direction,
                                    );
                                  } catch (e) {}
                                }}
                                className={`flex gap-1 text-xs min-h-[30px] cursor-pointer ${
                                  directionIndex === index
                                    ? "font-semibold text-[#A09200]"
                                    : "font-normal text-black"
                                }`}
                              >
                                <span className="min-w-[20px]">
                                  Step {index + 1} :
                                </span>
                                <span className="max-w-[200px]">
                                  {direction.instruction.replace(
                                    /<\/?[^>]+(>|$)/g,
                                    "",
                                  )}
                                </span>
                              </div>
                            </Fragment>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-row justify-between">
                    {mapStore?.directions?.length > 0 &&
                    mapStore.isWayfindingFinished ? (
                      <div
                        className="bg-[#A09200] w-[110px] flex flex-row justify-center items-center py-3 text-white text-xs rounded-bl-2xl gap-x-2"
                        onClick={() => {
                          interactiveRef.current.replayPlayBackAnimation();
                        }}
                      >
                        <div className="text-center font-semibold text-xs text-white">
                          Replay
                        </div>
                        <img src={repeat} className="w-4 h-4" />
                      </div>
                    ) : (
                      <div
                        className="bg-[#A09200] w-[110px] flex flex-row justify-center items-center py-3 text-white text-xs rounded-bl-2xl"
                        onClick={() => {
                          mapStore.setIsWayfindingSkipped(true);
                        }}
                      >
                        Fast Forward
                      </div>
                    )}

                    <span
                      className="bg-[#EF3340] w-[110px] flex flex-row justify-center items-center py-3 text-white text-xs rounded-br-2xl"
                      onClick={() => {
                        resetWayfinding();
                      }}
                    >
                      End Navigation
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <AdsignMap
            key="floor-guide"
            sceneRef={sceneRef}
            cameraControlsRef={cameraControlsRef}
            interactiveRef={interactiveRef}
            wayfindingOption={{
              wayFindingType: "play-back",
              wayfindingSpeed: 3,
            }}
            mapOption={{
              hideUserLocation: true,
              highlightColor: "#4999A3",
              isShowAllFloor: false,
              floorLabel: {
                color: "white",
                size: 5,
              },
              isDisplayMinimap: false,
              maxZoom: 0,
              minZoom: 300,
              disableZoom: false,
              disablePanning: false,
              disableRotation: false,
              showPinOnActiveShape: false,
              autoFocusToDirection: true,
              usingStair: false,
              showIconOnTooltip: false,
            }}
            store={mapStore}
            onShapeClick={(shape) => {}}
          />
        </div>
      ) : (
        <div className="flex flex-col px-4 pt-8  h-full overflow-y-auto">
          <div className="flex px-6">
            <div class="flex items-center rounded-full overflow-hidden w-full max-w-md bg-white pl-4">
              <img src={searchIcon} className="aspect-square w-4 h-4 " />
              <input
                type="text"
                placeholder="Search"
                class="flex-1 px-3 py-2 placeholder-[#75787B80] text-black focus:outline-none"
                ref={searchRef}
                onFocus={handleFocus}
                onBlur={handleBlur}
                value={searchValue}
                onChange={(e) => {
                  setCurrentPage(1);
                  setSearchValue(e.target.value);
                }}
              />
            </div>
          </div>

          {isFocused && !searchValue ? (
            <></>
          ) : (
            <div className="grid grid-cols-3 gap-y-6 gap-x-2 pt-10 overflow-y-auto">
              {currentItems.map((data) => (
                <CardPoi
                  onClickCard={() => {
                    const shape = getPoiShapes(mapStore, data._id)[0];
                    if (mainStore.selectedDestinationPoi) {
                      if (
                        data?._id !== mainStore?.selectedDestinationPoi?._id
                      ) {
                        if (mapStore.startPOI && mapStore.startShape) {
                          setIsOpenWayfindingModal(false);
                          mainStore.setSelectedDestinationPoi(data);
                          mainStore.setSelectedDestinationShape(shape);
                          mapStore.setDestinationPOI(data);
                          mapStore.setDestinationShape(shape);
                          postSelectAnalytic(mapStore);
                        } else {
                          setIsOpenWayfindingModal(false);
                          mainStore.setSelectedStartPoi(data);
                          mainStore.setSelectedStartShape(shape);
                          mapStore.setStartPOI(data);
                          mapStore.setStartShape(shape);
                          postSelectAnalytic(mapStore);
                        }
                      }
                    } else {
                      if (data?._id !== mainStore?.selectedStartPoi?._id) {
                        // setOpenModalPoi(true);
                        setIsOpenWayfindingModal(false);
                        mainStore.setSelectedDestinationPoi(data);
                        mainStore.setSelectedDestinationShape(shape);
                        mapStore.setDestinationPOI(data);
                        mapStore.setDestinationShape(shape);
                        postSelectAnalytic(mapStore);
                      }
                    }

                    if (
                      mainStore.selectedStartPoi &&
                      mainStore.selectedDestinationPoi
                    ) {
                      // setOpenModalPoi(true);
                      setIsOpenWayfindingModal(true);
                    }
                  }}
                  data={data}
                />
              ))}
            </div>
          )}

          <NoResultFoundComponent
            isFocused={isFocused}
            searchValue={searchValue}
            currentItems={currentItems}
          />
          <div className="flex flex-1" />

          <div className="flex justify-center items-center gap-2 px-4 mb-2">
            <button
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>

            <button
              onClick={goToPrevious}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="flex justify-center items-center overflow-x-auto w-full max-w-[200px] scrollbar-hide">
              {renderPageNumbers()}
            </div>

            <button
              onClick={goToNext}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            <button
              onClick={goToLastPage}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col bg-[#215732] text-white px-4 py-6 h-[165px] items-center justify-center">
        <div className="flex w-full z-50">
          <div className="w-full h-[116px] mx-4 flex items-center bg-white rounded-xl shadow-lg px-3 gap-3">
            <img src={wayfinderIcon} className="h-[70px]" />
            <div className="flex-1 flex flex-col h-full gap-3 py-3">
              <div
                className="flex-1 flex items-center bg-black/5 rounded-md cursor-pointer"
                data-tut="from"
              >
                <span
                  className={`${
                    mapStore.startPOI ? "text-[#444444]" : "text-[#9C9C9C]"
                  } text-sm flex-1 px-3 line-clamp-1`}
                >
                  {mapStore.startPOI?.name ?? "Your Location"}
                </span>
                {mapStore.startPOI && (
                  <img
                    src={xCircleIcon}
                    className="flex-shrink-0 pe-2"
                    onClick={() => {
                      if (mapStore.directions.length > 0) {
                        resetWayfinding();
                      } else {
                        mainStore.setSelectedStartPoi(null);
                        mainStore.setSelectedStartShape(null);
                        mapStore.setStartPOI(null);
                        mapStore.setStartShape(null);
                        mapStore.setCurrentFreeShape([]);
                        setIsShowWayfinding(false);
                      }
                    }}
                  />
                )}
              </div>
              <div
                className="flex-1 flex items-center bg-black/5 rounded-md cursor-pointer"
                data-tut="destination"
              >
                <span
                  className={`${
                    mapStore.destinationPOI
                      ? "text-[#444444]"
                      : "text-[#9C9C9C]"
                  } text-sm flex-1 px-3 line-clamp-1`}
                >
                  {mapStore.destinationPOI?.name ?? "Where To?"}
                </span>
                {mapStore.destinationPOI && (
                  <img
                    src={xCircleIcon}
                    className="flex-shrink-0 pe-2"
                    onClick={() => {
                      if (mapStore.directions.length > 0) {
                        resetWayfinding();
                      } else {
                        mainStore.setSelectedDestinationPoi(null);
                        mainStore.setSelectedDestinationShape(null);
                        mapStore.setDestinationPOI(null);
                        mapStore.setDestinationShape(null);
                        mapStore.setCurrentFreeShape([]);
                        setIsShowWayfinding(false);
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* {isOpenWayfindingModal && (
        <ModalStartWayfinding
          closeModal={() => setIsOpenWayfindingModal(false)}
          locationName={mainStore.selectedDestinationPoi?.name ?? ""}
          startWayfinding={() => startWayfinding()}
        />
      )} */}

      {openModalPoi && (
        <ModalDetailPoi
          data={mainStore.selectedDestinationPoi ?? mainStore.selectedStartPoi}
          onClose={() => {
            setOpenModalPoi(false);
            if (
              mapStore.startPOI &&
              mapStore.startShape &&
              mapStore.destinationPOI &&
              mapStore.destinationShape
            ) {
              mapStore.setDestinationPOI(null);
              mapStore.setDestinationShape(null);
              mainStore.setSelectedDestinationPoi(null);
              mainStore.setSelectedDestinationShape(null);
            }
          }}
          config={mapStore?.config}
          isOpenWayfindingModal={isOpenWayfindingModal}
          setStart={() => {
            mapStore.setStartPOI(mainStore.selectedStartPoi);
            mapStore.setStartShape(mainStore.selectedStartShape);
          }}
          setEnd={() => {
            mapStore.setDestinationPOI(mainStore.selectedDestinationPoi);
            mapStore.setDestinationShape(mainStore.selectedDestinationShape);
          }}
          startWayfinding={() => {
            setOpenModalPoi(false);
            startWayfinding();
          }}
        />
      )}
      {openModalStartLoc && (
        <ModalStartLocationFromQR
          name={mainStore.selectedStartPoi.name}
          onClose={() => setOpenModalStartLoc(false)}
        />
      )}
    </Layout>
  );
});

export default WayfinderPage;
